import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const CLAPPER_IMG =
  "https://images.unsplash.com/photo-1515634928627-2a4e0dae3ddf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxjaW5lbWF0aWMlMjBjbGFwcGVyYm9hcmR8ZW58MHx8fHwxNzg3NzQwNzkzfDA&ixlib=rb-4.1.0&q=85";

const CHAPTERS = [
  {
    no: "01",
    title: "Commercial Management",
    tagline: "Plan. Negotiate. Control.",
    items: [
      "Budgeting & Cost Planning",
      "Vendor Management",
      "Rate Negotiation",
      "Purchase / Work Orders",
      "Cost Commitments",
      "Department Budgets",
      "Estimate vs Actual",
    ],
  },
  {
    no: "02",
    title: "Project Audit",
    tagline: "Monitor. Verify. Ensure.",
    items: [
      "Running Shoot Audit",
      "Daily Cost Verification",
      "Crew & Vendor Verification",
      "Production Expense Audit",
      "Supporting Documents",
      "Budget Variance",
      "Post-Production Audit",
    ],
    image: CLAPPER_IMG,
    imageAlt: "Clapperboard on a dark production desk",
  },
  {
    no: "03",
    title: "Project Accounting",
    tagline: "Record. Reconcile. Report.",
    items: [
      "Invoices & Payments",
      "Advances & Accruals",
      "Ledger Management",
      "Vendor Reconciliation",
      "Cost Allocation",
      "Financial Reporting",
      "Final Cost & Closure",
    ],
  },
];

export default function Services() {
  const [active, setActive] = useState(null);

  return (
    <section id="services" data-testid="services-section" className="relative overflow-hidden py-24 md:py-32">
      <img
        src="/assets/veritas-seal.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -right-44 top-16 h-[560px] w-[560px] select-none opacity-[0.05] mix-blend-lighten"
      />
      <div className="relative mx-auto max-w-[1440px] px-5 md:px-10">
        <Reveal>
          <p className="mb-4 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">
            <span className="h-px w-10 bg-[#B79045]" /> Our Services
          </p>
          <h2 className="max-w-3xl font-display text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            Complete Financial Control for{" "}
            <em className="italic text-[#D4AF37]">Media &amp; Film</em> Productions
          </h2>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-neutral-400 md:text-base">
            We bring Commercial Management, Project Audit and Accounting into a
            single connected system — giving you real-time visibility, stronger
            control and a smoother financial closure.
          </p>
        </Reveal>

        <div className="mt-16 md:mt-24">
          {CHAPTERS.map((ch, i) => (
            <Reveal key={ch.no} delay={i * 0.08}>
              <div
                data-testid={`service-chapter-${ch.no}`}
                onMouseEnter={() => setActive(i)}
                onMouseLeave={() => setActive(null)}
                className={`grid grid-cols-1 gap-8 border-t border-white/10 py-12 transition-opacity duration-500 md:grid-cols-12 md:py-16 ${
                  active !== null && active !== i ? "opacity-35" : "opacity-100"
                }`}
              >
                <div className="md:col-span-2">
                  <span className="font-mono text-4xl font-bold text-[#B79045] md:text-5xl">
                    {ch.no}
                  </span>
                </div>

                <div className="md:col-span-4">
                  <h3 className="font-display text-3xl font-medium tracking-tight text-neutral-100 md:text-4xl">
                    {ch.title}
                  </h3>
                  <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">
                    {ch.tagline}
                  </p>
                  <ArrowUpRight
                    size={20}
                    className={`mt-6 text-[#D4AF37] transition-transform duration-500 ${
                      active === i ? "translate-x-1 -translate-y-1" : ""
                    }`}
                  />
                </div>

                <div className="md:col-span-6">
                  <ul className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
                    {ch.items.map((item) => (
                      <li
                        key={item}
                        className="flex items-center gap-3 border-b border-white/5 py-3 text-sm text-neutral-300"
                      >
                        <span className="h-1 w-1 bg-[#B79045]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  {ch.image && (
                    <div className="relative mt-8 overflow-hidden">
                      <img
                        src={ch.image}
                        alt={ch.imageAlt}
                        loading="lazy"
                        className="h-48 w-full object-cover md:h-56"
                      />
                      <div
                        className="absolute inset-0"
                        style={{
                          background:
                            "radial-gradient(ellipse at 50% 0%, transparent 30%, rgba(5,5,5,0.75) 100%)",
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
          <div className="border-t border-white/10" />
        </div>
      </div>
    </section>
  );
}
